import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * 3D Helical Spiral Circular Showcase
 * Arranges project cards along a vertical 3D helix cylinder.
 * Rotating the spiral via scroll or drag glides the active card to the front center,
 * revealing its domain eyebrows, title, short description, and CTA button.
 */
export class SpiralShowcase {
  constructor(wrapperId = 'spiral-showcase-wrapper', options = {}) {
    this.wrapper = document.getElementById(wrapperId);
    if (!this.wrapper) return;

    this.cosmicScene = options.cosmicScene || null;
    this.stage = this.wrapper.querySelector('.spiral-cylinder-stage');
    this.cards = Array.from(this.wrapper.querySelectorAll('.spiral-card'));

    if (!this.cards.length) return;

    this.cardCount = this.cards.length;
    this.activeIndex = 0;
    this.currentProgress = 0; // 0 to 1
    this.targetProgress = 0;
    this.isDragging = false;
    this.isAutoSnapping = false;
    this.preventClick = false;
    this.dragDistance = 0;

    // Geometry configuration for smooth vertical corkscrew with enlarged cards
    this.radius = 420;
    this.verticalPitch = 74; // Vertical height offset per card
    this.angleStep = 32; // Degrees rotation per card along helix

    this.init();
  }

  init() {
    this.updateDimensions();
    window.addEventListener('resize', () => this.updateDimensions(), { passive: true });

    // Initial render
    this.applyScrollChoreography(0);

    // 1. Pinned ScrollTrigger scrubbing
    this.initScrollTrigger();

    // 2. Inactive card click centering & Lightbox on active card click
    this.initCardInteractions();
  }

  updateDimensions() {
    const w = window.innerWidth;
    if (w < 420) {
      this.radius = 180;
      this.verticalPitch = 44;
      this.angleStep = 40;
    } else if (w < 640) {
      this.radius = 250;
      this.verticalPitch = 54;
      this.angleStep = 38;
    } else if (w < 1024) {
      this.radius = 340;
      this.verticalPitch = 64;
      this.angleStep = 34;
    } else {
      this.radius = 420;
      this.verticalPitch = 74;
      this.angleStep = 32;
    }

    this.renderHelix(this.currentProgress);
  }

  initScrollTrigger() {
    // Slower, more deliberate pacing per card with generous scroll room
    const scrollDistance = Math.max(6000, this.cardCount * 580 + 2800);

    this.trigger = ScrollTrigger.create({
      trigger: this.wrapper,
      start: 'center center',
      end: () => `+=${scrollDistance}`,
      pin: true,
      scrub: 0.65,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        if (!this.isDragging && !this.isAutoSnapping) {
          this.applyScrollChoreography(self.progress);
        }
      },
    });
  }

  /**
   * Unified Choreographed Scroll Sequence:
   * 1. [0.00, 0.15]: Orbiting Rays & Circle Formation (Cards hidden)
   * 2. [0.15, 0.23]: First image zooms in from tiny in circle center
   * 3. [0.23, 0.85]: Smooth natural 3D card deck transitions
   * 4. [0.85, 1.00]: Last image zooms out to tiny center + dark core pushes white circle outward
   */
  applyScrollChoreography(p) {
    let rayProgress = 0.0;
    let whiteIntensity = 0.0;
    let inwardProgress = 0.0;
    let whiteSpread = 0.0;
    let darkCore = 0.0;

    let carouselProgress = 0.0;
    let zoomFactor = 1.0;
    let zoomOutFactor = 1.0;

    // Scroll-driven spin for the cosmos WebGL shader
    const scrollSpin = p * 15.0;

    if (p < 0.15) {
      // Phase 1: Rays stream in and orbit circumference to complete circle with white ring
      rayProgress = p / 0.15;
      whiteIntensity = Math.max(0.0, (p - 0.07) / 0.08);
      inwardProgress = 0.0;
      whiteSpread = 0.0;
      darkCore = 0.0;

      // Cards remain completely hidden until circle completes
      carouselProgress = 0.0;
      zoomFactor = 0.0;

      if (this.stage) {
        this.stage.style.opacity = '0';
        this.stage.style.pointerEvents = 'none';
      }
    } else if (p < 0.23) {
      // Phase 2A: First image zooms in from the center of the circle from tiny
      rayProgress = 1.0;
      whiteIntensity = 1.0;
      darkCore = 0.0;

      const u_inward = (p - 0.15) / 0.7;
      inwardProgress = u_inward;
      whiteSpread = u_inward;

      // Stay on card 0 while it zooms up from tiny
      carouselProgress = 0.0;

      const tZoomIn = (p - 0.15) / 0.08;
      // Smooth cubic ease: 0 -> 1
      zoomFactor = tZoomIn * tZoomIn * (3 - 2 * tZoomIn);

      if (this.stage) {
        this.stage.style.opacity = '1';
        this.stage.style.pointerEvents = zoomFactor > 0.4 ? 'auto' : 'none';
      }
    } else if (p <= 0.85) {
      // Phase 2B: Smooth stack progression across all 15 images
      rayProgress = 1.0;
      whiteIntensity = 1.0;
      darkCore = 0.0;

      const u_inward = (p - 0.15) / 0.7;
      inwardProgress = Math.min(1.0, u_inward);
      whiteSpread = Math.min(1.0, u_inward);

      // Deck progress from card 0 to card 14
      const u_deck = (p - 0.23) / (0.85 - 0.23);
      carouselProgress = u_deck;
      zoomFactor = 1.0;
      zoomOutFactor = 1.0;

      if (this.stage) {
        this.stage.style.opacity = '1';
        this.stage.style.pointerEvents = 'auto';
      }
    } else {
      // Phase 3: Last image zooms out into tiny in the center + dark void inhales into background
      rayProgress = 1.0;
      whiteIntensity = 1.0;
      inwardProgress = 1.0;
      whiteSpread = 1.0;
      carouselProgress = 1.0; // Anchored on final 15th card

      const tVoid = (p - 0.85) / 0.15;
      darkCore = Math.min(1.0, tVoid);

      // Smooth cubic shrink into center tiny part: 1 -> 0
      const smoothVoid = tVoid * tVoid * (3 - 2 * tVoid);
      zoomOutFactor = Math.max(0.001, 1.0 - smoothVoid);
      zoomFactor = 1.0;

      if (this.stage) {
        this.stage.style.opacity = '1';
        this.stage.style.pointerEvents = zoomOutFactor > 0.3 ? 'auto' : 'none';
      }
    }

    // Drive 3D stacked depth card positions smoothly
    this.currentProgress = carouselProgress;
    this.renderHelix(carouselProgress, zoomFactor, zoomOutFactor);

    // Drive WebGL shader parameters (including scrollSpin)
    if (this.cosmicScene) {
      this.cosmicScene.setScrollState({
        rayProgress,
        whiteIntensity,
        inwardProgress,
        whiteSpread,
        darkCore,
        scrollSpin,
      });
    }
  }

  /**
   * 3D Depth Stacking with Natural Scroll Physics:
   * Active card is displayed front and center, framed within the cosmic circle.
   * As you scroll down:
   * - The exiting card glides smoothly UPWARDS with a gentle 3D tilt, fading away cleanly.
   * - The next card underneath smoothly rises and expands forward from the depth stack into full focus.
   * - Background cards rest neatly in subtle depth layers without visual clutter or ghosting.
   */
  renderHelix(progress, zoomFactor = 1.0, zoomOutFactor = 1.0) {
    const virtualIndex = progress * (this.cardCount - 1);
    this.activeIndex = Math.round(virtualIndex);

    this.cards.forEach((card, idx) => {
      // Relative offset from active center card (continuous float)
      const d = idx - virtualIndex;

      // Cards that have already exited completely (d < -1.1)
      // or cards too far back in the queue (d > 4.5)
      // or before initial zoom-in starts (zoomFactor <= 0.001)
      if (d < -1.1 || d > 4.5 || zoomFactor <= 0.001) {
        card.style.opacity = '0';
        card.style.pointerEvents = 'none';
        card.style.transform = 'translate3d(0, 0, -400px) scale(0.01)';
        card.classList.remove('is-active-card');
        card.classList.add('is-background-card');
        return;
      }

      let tx = 0;
      let ty = 0;
      let tz = 0;
      let rotX = 0;
      let scale = 1.0;
      let opacity = 1.0;
      let zIndex = 100;

      if (d >= 0) {
        // Active card (d = 0) and cards stacked in depth behind it (d > 0)
        // Resting neatly in depth:
        tz = -d * 72; // deep Z layering
        ty = d * 18; // slightly lower so active front card dominates and doesn't get obscured
        rotX = -d * 2.0; // subtle resting perspective tilt
        scale = Math.max(0.35, 1.0 - d * 0.065);
        opacity = Math.max(0.0, Math.min(1.0, 1.0 - d * 0.24));
        zIndex = Math.max(1, Math.round(110 - d * 6));
      } else {
        // Exiting card (d < 0): As user scrolls down, old card smoothly glides UPWARDS and away
        ty = d * 135; // smoothly translates upwards (-135px at d = -1)
        tz = d * 30; // recedes slightly into depth (-30px at d = -1)
        rotX = -d * 7.5; // gentle luxury 3D flick angle (+7.5deg at d = -1)
        scale = Math.max(0.82, 1.0 + d * 0.07); // subtly shrinks as it lifts away
        // Smooth, gradual fade out: comfortable to track with the eye
        opacity = Math.max(0.0, Math.min(1.0, Math.pow(Math.max(0.0, 1.0 + d * 1.35), 1.3)));
        zIndex = 125; // stays on top while lifting away
      }

      // First image emergence: zoom in from center from tiny
      if (zoomFactor < 1.0) {
        scale *= Math.max(0.01, zoomFactor);
        opacity *= zoomFactor;
      }

      // Last image exit: zoom out into center tiny part
      if (zoomOutFactor < 1.0 && idx === this.cardCount - 1) {
        scale *= Math.max(0.01, zoomOutFactor);
        opacity *= Math.max(0.0, zoomOutFactor * 1.25);
      }

      card.style.transform = `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, ${tz.toFixed(1)}px) rotateX(${rotX.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
      card.style.opacity = Math.max(0.0, Math.min(1.0, opacity)).toFixed(4);
      card.style.zIndex = `${zIndex}`;

      const isCurrentActive = Math.abs(d) < 0.45;
      card.style.pointerEvents = opacity > 0.25 ? 'auto' : 'none';

      if (isCurrentActive) {
        card.classList.add('is-active-card');
        card.classList.remove('is-background-card');
      } else {
        card.classList.remove('is-active-card');
        card.classList.add('is-background-card');
      }
    });
  }

  goToCard(index) {
    if (index < 0 || index >= this.cardCount) return;

    this.isAutoSnapping = true;
    const targetProgress = index / (this.cardCount - 1);

    gsap.to(this, {
      currentProgress: targetProgress,
      duration: 0.85,
      ease: 'power3.out',
      onUpdate: () => {
        this.renderHelix(this.currentProgress);
      },
      onComplete: () => {
        this.isAutoSnapping = false;
        // Sync trigger scroll if available
        if (this.trigger) {
          const scrollTarget =
            this.trigger.start + targetProgress * (this.trigger.end - this.trigger.start);
          if (window.nbkLenis) {
            window.nbkLenis.scrollTo(scrollTarget, { immediate: true });
          } else {
            window.scrollTo({ top: scrollTarget, behavior: 'instant' });
          }
        }
      },
    });
  }

  initDragPhysics() {
    let startY = 0;
    let startProgress = 0;
    let lastY = 0;
    let velocity = 0;
    let lastTime = 0;

    const onPointerDown = (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      this.isDragging = true;
      this.preventClick = false;
      this.dragDistance = 0;
      startY = e.clientY;
      lastY = e.clientY;
      lastTime = window.performance ? window.performance.now() : Date.now();
      velocity = 0;
      startProgress = this.currentProgress;

      try {
        this.stage.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    };

    const onPointerMove = (e) => {
      if (!this.isDragging) return;

      const deltaY = e.clientY - startY;
      this.dragDistance = Math.max(this.dragDistance, Math.abs(deltaY));

      if (this.dragDistance > 6) {
        this.preventClick = true;
      }

      // Drag sensitivity
      const dragFactor = (this.cardCount - 1) * 0.0018;
      let newProgress = startProgress - deltaY * dragFactor;
      newProgress = Math.max(0, Math.min(1, newProgress));

      const now = window.performance ? window.performance.now() : Date.now();
      const dt = now - lastTime;
      if (dt > 12) {
        velocity = (e.clientY - lastY) / dt;
        lastY = e.clientY;
        lastTime = now;
      }

      this.currentProgress = newProgress;
      this.renderHelix(newProgress);
    };

    const onPointerUp = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;

      try {
        if (this.stage.hasPointerCapture(e.pointerId)) {
          this.stage.releasePointerCapture(e.pointerId);
        }
      } catch {
        // ignore
      }

      if (this.dragDistance > 6) {
        // Apply inertia release then snap to nearest card
        const projectedProgress = this.currentProgress - velocity * 0.08;
        const targetIdx = Math.max(
          0,
          Math.min(this.cardCount - 1, Math.round(projectedProgress * (this.cardCount - 1))),
        );
        this.goToCard(targetIdx);
      }
    };

    if (this.stage) {
      this.stage.addEventListener('pointerdown', onPointerDown);
      this.stage.addEventListener('pointermove', onPointerMove);
      this.stage.addEventListener('pointerup', onPointerUp);
      this.stage.addEventListener('pointercancel', onPointerUp);
    }
  }

  initCardInteractions() {
    this.cards.forEach((card, idx) => {
      card.addEventListener('click', (e) => {
        // If user clicked the CTA button link, let the browser navigate naturally
        if (e.target.closest('a')) {
          return;
        }

        // If user clicked an inactive card, rotate it to the center
        if (idx !== this.activeIndex) {
          e.preventDefault();
          e.stopPropagation();
          this.goToCard(idx);
          return;
        }

        // If user clicked the active front card's image, open lightbox
        const img = card.querySelector('img');
        if (!img) return;

        if (window.nbkLightbox) {
          const title = card.getAttribute('data-title') || 'Project Preview';
          const link = card.getAttribute('data-link') || '';
          window.nbkLightbox.open({
            images: [{ url: img.src, alt: title }],
            index: 0,
            title,
            link,
            btnText: 'VIEW PROJECT',
          });
          return;
        }

        const lightbox = document.getElementById('nbk-lightbox-modal');
        const lbImg = document.getElementById('nbk-lightbox-img');
        const lbTitle = document.getElementById('nbk-lightbox-title');
        const lbCtaBtn = document.getElementById('nbk-lightbox-cta-btn');
        const lbCtaText = document.getElementById('nbk-lightbox-cta-text');

        if (lightbox && lbImg) {
          lbImg.src = img.src;
          if (lbTitle) lbTitle.textContent = card.getAttribute('data-title') || 'Project Preview';
          const link = card.getAttribute('data-link');
          if (lbCtaBtn && link) {
            lbCtaBtn.href = link;
          }
          if (lbCtaText) {
            lbCtaText.textContent = 'VIEW PROJECT';
          }
          lightbox.classList.add('opacity-100', 'pointer-events-auto');
          lightbox.classList.remove('opacity-0', 'pointer-events-none');
          lightbox.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }
}
